<template>
  <q-table
    ref="component"
    :columns="visibleColumns"
    :table-header-class="tableHeaderClass"
    :virtual-scroll-sticky-size-start="48"
    card-class="full-height bg-neutral-20"
    table-class="full-height"
    color="primary-100"
    flat
    virtual-scroll
  >
    <template #header-selection="scope">
      <slot name="header-selection">
        <q-checkbox v-model="scope.selected" />
      </slot>
    </template>

    <template #body-selection="scope">
      <slot name="body-selection">
        <q-checkbox v-model="scope.selected" />
      </slot>
    </template>
    <template
      v-for="(slot, index) of Object.keys($slots)"
      :key="index"
      #[slot]="slotProps"
    >
      <slot v-if="slotProps" v-bind="slotProps" :name="slot"></slot>
      <slot v-else :name="slot"></slot>
    </template>

    <template v-if="expandableRows" #header="headerProps">
      <q-tr :class="tableHeaderClass" :props="headerProps">
        <q-th class="expand-toggle" auto-width />

        <q-th
          v-for="col in headerProps.cols"
          :key="col.name"
          :props="headerProps"
          class="ellipsis"
        >
          {{ col.label }}
        </q-th>
      </q-tr>
    </template>

    <template v-if="expandableRows" #body="bodyProps">
      <q-tr :key="`m_${bodyProps.rowIndex}`" :props="bodyProps">
        <q-td v-if="expandableRows" class="expand-toggle">
          <ExpandToggleCell
            :dense="$attrs.dense !== undefined"
            :expanded="isExpanded(bodyProps.rowIndex)"
            @click="toggleExpandedRow(bodyProps.rowIndex)"
          />
        </q-td>
        <slot name="row" v-bind="bodyProps">
          <span>Row</span>
        </slot>
      </q-tr>
      <q-tr
        v-if="isExpanded(bodyProps.rowIndex)"
        :key="`m_${bodyProps.rowIndex}`"
        :props="bodyProps"
        class="q-virtual-scroll--with-prev"
      >
        <slot name="expanded-row" v-bind="bodyProps">
          <span>Expanded Row</span>
        </slot>
      </q-tr>
    </template>

    <template #no-data="{ message }">
      <div class="text-center full-width">
        <span class="text-sm text-neutral-90">{{ message }}</span>
      </div>
    </template>
  </q-table>
</template>

<script setup>
const component = ref(null);

const expandedRows = ref([]);

const toggleExpandedRow = (currentRowIndex) => {
  if (unref(expandedRows).includes(currentRowIndex)) {
    expandedRows.value = unref(expandedRows).filter(
      (rowIndex) => rowIndex !== currentRowIndex,
    );
  } else {
    expandedRows.value = [...unref(expandedRows), currentRowIndex];
  }
};

const isExpanded = (rowIndex) => unref(expandedRows).includes(rowIndex);

defineExpose({
  component,
  isExpanded,
  toggleExpandedRow,
});

const props = defineProps({
  columns: {
    type: Array,
    required: true,
  },
  expandableRows: {
    type: Boolean,
    default: false,
  },
  tableHeaderClass: {
    type: String,
    default: "bg-primary-10 text-neutral",
  },
});

const visibleColumns = computed(() =>
  unref(props.columns).filter((col) => !col.hidden),
);
</script>

<style lang="scss" scoped>
.q-table__container {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;

  :deep(.q-table__top) {
    align-items: flex-start;

    padding: 16px 0;

    & ~ .q-table__bottom {
      border-radius: 8px 8px 0 0 !important;
    }
  }

  :deep(.q-table__middle) {
    background-color: $neutral-10;

    order: 1;

    th {
      font-size: 12px;
      line-height: 1.33;
      position: sticky;
      top: 0;
      z-index: 1;

      &.expand-toggle {
        min-width: 40px;
        padding: 7px 4px;
      }
    }

    tbody {
      tr {
        .expand-toggle {
          padding: 7px 4px;
          .q-btn {
            &:not(.expanded) {
              display: none;
            }
          }
        }
      }

      td {
        color: $neutral;
        font-size: 12px;
        line-height: 1.33;

        .q-badge {
          font-size: 12px;
        }

        &:before {
          display: none;
        }
      }
    }

    th,
    td {
      &.sticky {
        &--left {
          box-shadow: 3px 0px 5px 0px $neutral-30;
          left: 0;
          position: sticky;
          z-index: 2;
        }

        &--right {
          box-shadow: -3px 0px 5px 0px $neutral-30;
          position: sticky;
          right: 0;
        }
      }
    }

    td {
      &.sticky {
        &--left,
        &--right {
          background-color: $neutral-10;
        }
      }
    }
  }

  .no-custom-order {
    :deep(.q-table__middle) {
      order: initial;
    }
  }

  & > :deep(.q-table__middle) {
    tbody {
      tr {
        &:hover {
          .expand-toggle {
            .q-btn {
              display: block;
            }
          }
        }

        &:not([class*="bg"]) {
          &:hover {
            & > td {
              background: $primary-20;
            }
          }
        }
      }
    }
  }

  &.q-table--dense {
    :deep(.q-table__middle) {
      th {
        &.expand-toggle {
          min-width: 34px;
          padding: 0 4px;
        }
      }

      tbody {
        tr {
          .expand-toggle {
            padding: 0 4px;
          }
        }
      }
    }
  }

  :deep(.q-table__bottom) {
    background-color: $neutral-10;
    padding-top: 8px;
    padding-bottom: 8px;
    border-radius: 0;
    border-top: none;

    .q-table {
      &__bottom-item {
        color: $neutral-90;
        font-size: 14px;
        line-height: 1.33;

        .q-field {
          &__inner {
            background-color: $primary-30;
            border-radius: 28px;
            padding: 0 1em;
          }
        }
      }
    }
  }
}
</style>
